import { useRef, useState } from 'react';

export default function ProjectsSection() {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  const projects = [
    {
      code: 'TETRA-01',
      name: 'Thunderbolt',
      year: '2024',
      category: 'Formula Student',
      image: 'https://readdy.ai/api/search-image?query=formula%20student%20electric%20racing%20car%20side%20view%20carbon%20fiber%20body%20aerodynamic%20design%20professional%20photography%20dark%20background%20blue%20lighting%20modern%20engineering%20sleek%20vehicle%20motorsport%20competition&width=480&height=400&seq=project-1&orientation=portrait',
      tags: ['Carbon Fiber', 'Electric', 'Autonomous'],
      specs: ['Motor Gücü: 80 kW', '0-100: 3.2 sn', 'Ağırlık: 220 kg']
    },
    {
      code: 'TETRA-02',
      name: 'Phoenix',
      year: '2023',
      category: 'Formula Student',
      image: 'https://readdy.ai/api/search-image?query=formula%20student%20racing%20car%20front%20three%20quarter%20view%20red%20accents%20carbon%20fiber%20aerodynamic%20wings%20professional%20studio%20lighting%20dark%20background%20high%20performance%20vehicle%20motorsport%20engineering&width=480&height=400&seq=project-2&orientation=portrait',
      tags: ['Hybrid', 'Advanced Aero', 'Telemetry'],
      specs: ['Motor Gücü: 75 kW', '0-100: 3.5 sn', 'Ağırlık: 235 kg']
    },
    {
      code: 'TETRA-03',
      name: 'Velocity',
      year: '2022',
      category: 'Formula Student',
      image: 'https://readdy.ai/api/search-image?query=formula%20student%20racing%20car%20rear%20view%20large%20rear%20wing%20carbon%20fiber%20body%20professional%20photography%20dark%20background%20orange%20accent%20lights%20high%20tech%20motorsport%20vehicle%20engineering&width=480&height=400&seq=project-3&orientation=portrait',
      tags: ['Combustion', 'Lightweight', 'Performance'],
      specs: ['Motor Gücü: 70 kW', '0-100: 3.8 sn', 'Ağırlık: 210 kg']
    },
    {
      code: 'TETRA-04',
      name: 'Apex',
      year: '2021',
      category: 'Formula Student',
      image: 'https://readdy.ai/api/search-image?query=formula%20student%20racing%20car%20top%20view%20aerodynamic%20design%20carbon%20fiber%20body%20professional%20photography%20dark%20background%20blue%20lighting%20modern%20engineering%20motorsport%20vehicle%20competition&width=480&height=400&seq=project-4&orientation=portrait',
      tags: ['Electric', 'Data Analytics', 'Efficiency'],
      specs: ['Motor Gücü: 65 kW', '0-100: 4.0 sn', 'Ağırlık: 225 kg']
    },
    {
      code: 'TETRA-05',
      name: 'Genesis',
      year: '2020',
      category: 'Formula Student',
      image: 'https://readdy.ai/api/search-image?query=formula%20student%20racing%20car%20detail%20shot%20suspension%20system%20carbon%20fiber%20components%20professional%20photography%20dark%20background%20technical%20engineering%20motorsport%20vehicle%20high%20detail&width=480&height=400&seq=project-5&orientation=portrait',
      tags: ['Prototype', 'Innovation', 'First Gen'],
      specs: ['Motor Gücü: 60 kW', '0-100: 4.2 sn', 'Ağırlık: 240 kg']
    }
  ];

  const scroll = (direction: 'left' | 'right') => {
    if (scrollRef.current) {
      const scrollAmount = 520;
      scrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  return (
    <section id="projeler" className="relative bg-black py-32 px-8 overflow-hidden">
      {/* Section Header */}
      <div className="max-w-7xl mx-auto text-center mb-20">
        <h3 className="text-[#00d9ff] text-sm uppercase tracking-[0.3em] font-semibold mb-4">
          PROJELER
        </h3>
        <h2 className="text-6xl font-bold mb-6">Yarış Pistindeki Eserlerimiz</h2>
        <div className="w-32 h-1 bg-gradient-to-r from-transparent via-[#00d9ff] to-transparent mx-auto animate-expand" />
      </div>

      {/* Projects Gallery */}
      <div className="relative max-w-[1600px] mx-auto">
        <div
          ref={scrollRef}
          className="flex gap-10 overflow-x-auto scrollbar-hide snap-x snap-mandatory scroll-smooth pb-8"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {projects.map((project, index) => (
            <div
              key={index}
              className="flex-shrink-0 w-[480px] snap-center"
              onMouseEnter={() => setHoveredIndex(index)}
              onMouseLeave={() => setHoveredIndex(null)}
            >
              <div className="group relative bg-[#0d0d0d] rounded-lg overflow-hidden transition-all duration-500 hover:-translate-y-2 hover:shadow-[0_20px_60px_rgba(0,217,255,0.3)] cursor-pointer h-[620px]">
                {/* Image Area */}
                <div className="relative h-[400px] overflow-hidden">
                  <img
                    src={project.image}
                    alt={project.name}
                    className={`w-full h-full object-cover object-center transition-all duration-700 ${
                      hoveredIndex === index ? 'grayscale-0 scale-110' : 'grayscale scale-100'
                    }`}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0d0d0d] via-transparent to-transparent" />

                  {/* Hover Overlay with Specs */}
                  <div className={`absolute inset-0 bg-black/90 flex items-center justify-center transition-opacity duration-500 ${
                    hoveredIndex === index ? 'opacity-100' : 'opacity-0 pointer-events-none'
                  }`}>
                    <div className="text-center space-y-6 px-8">
                      <h4 className="text-2xl font-bold text-[#00d9ff] mb-4">Teknik Özellikler</h4>
                      {project.specs.map((spec, i) => (
                        <div key={i} className="text-lg text-white/80 flex items-center justify-center gap-3">
                          <div className="w-2 h-2 bg-[#00d9ff] rounded-full" />
                          {spec}
                        </div>
                      ))}
                      <button className="mt-8 px-8 py-3 border-2 border-[#ff6b35] text-white rounded-full hover:bg-[#ff6b35] hover:text-black transition-all duration-300 whitespace-nowrap cursor-pointer group/btn">
                        <span className="flex items-center gap-2">
                          Detayları Gör
                          <i className="ri-arrow-right-line transition-transform duration-300 group-hover/btn:translate-x-2" />
                        </span>
                      </button>
                    </div>
                  </div>
                </div>

                {/* Info Area */}
                <div className="p-8 space-y-4">
                  <div className="text-xs text-[#00d9ff] uppercase tracking-[0.2em] font-semibold">
                    {project.code}
                  </div>
                  <h3 className="text-3xl font-bold text-white">{project.name}</h3>
                  <div className="text-sm text-white/60">
                    {project.year} | {project.category}
                  </div>
                  <div className="flex flex-wrap gap-2 pt-2">
                    {project.tags.map((tag, i) => (
                      <span
                        key={i}
                        className="px-4 py-1.5 text-xs border border-[#00d9ff]/30 rounded-full text-white/70 hover:border-[#00d9ff] hover:text-white transition-colors duration-300 cursor-pointer whitespace-nowrap"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Navigation Buttons */}
        <button
          onClick={() => scroll('left')}
          className="absolute left-0 top-1/2 -translate-y-1/2 w-14 h-14 bg-[#00d9ff]/10 backdrop-blur-lg border border-[#00d9ff]/30 rounded-full flex items-center justify-center hover:bg-[#00d9ff] hover:scale-110 transition-all duration-300 cursor-pointer group"
        >
          <i className="ri-arrow-left-line text-2xl text-[#00d9ff] group-hover:text-black" />
        </button>
        <button
          onClick={() => scroll('right')}
          className="absolute right-0 top-1/2 -translate-y-1/2 w-14 h-14 bg-[#00d9ff]/10 backdrop-blur-lg border border-[#00d9ff]/30 rounded-full flex items-center justify-center hover:bg-[#00d9ff] hover:scale-110 transition-all duration-300 cursor-pointer group"
        >
          <i className="ri-arrow-right-line text-2xl text-[#00d9ff] group-hover:text-black" />
        </button>
      </div>

      <style>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        @keyframes expand {
          from { width: 0; }
          to { width: 128px; }
        }
        .animate-expand {
          animation: expand 1s ease-out forwards;
        }
      `}</style>
    </section>
  );
}
